import type { FC } from 'react';

import Button from '@/common-components/Button';
import type { PaymentDateBasis } from '@/types/payment.types';

import type { PaymentRangeErrors } from '../hooks/usePaymentsPage.hook';

interface PaymentsExportBarProps {
  from: string;
  to: string;
  basis: PaymentDateBasis;
  isExporting: boolean;
  exportError: string | null;
  rangeErrors: PaymentRangeErrors;
  onChangeFrom: (value: string) => void;
  onChangeTo: (value: string) => void;
  onChangeBasis: (value: PaymentDateBasis) => void;
  onExport: () => void;
}

const fieldStyles =
  'h-8 rounded-lg border border-[var(--color-divider)] bg-[var(--color-surface-muted)] px-3 text-[12.5px] font-medium text-[var(--color-ink-muted)] focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30';
const selectStyles = `${fieldStyles} cursor-pointer appearance-none pr-8`;
const errorStyles = 'border-[var(--color-danger)] focus:border-[var(--color-danger)]';
const labelStyles = 'text-[11.5px] font-semibold text-[var(--color-ink-muted)]';

const PaymentsExportBar: FC<PaymentsExportBarProps> = ({
  from,
  to,
  basis,
  isExporting,
  exportError,
  rangeErrors,
  onChangeFrom,
  onChangeTo,
  onChangeBasis,
  onExport,
}) => (
  <div className="flex flex-col gap-2 border-b border-[var(--color-divider)] px-4 py-3">
    <div className="flex flex-wrap items-end gap-3">
      <label className="flex flex-col gap-1">
        <span className={labelStyles}>From</span>
        <input
          type="date"
          value={from}
          onChange={(e) => onChangeFrom(e.target.value)}
          className={`${fieldStyles} ${rangeErrors.from ? errorStyles : ''}`}
        />
      </label>

      <label className="flex flex-col gap-1">
        <span className={labelStyles}>To</span>
        <input
          type="date"
          value={to}
          onChange={(e) => onChangeTo(e.target.value)}
          className={`${fieldStyles} ${rangeErrors.to ? errorStyles : ''}`}
        />
      </label>

      <label className="flex flex-col gap-1">
        <span className={labelStyles}>Date basis</span>
        <select
          value={basis}
          onChange={(e) => onChangeBasis(e.target.value as PaymentDateBasis)}
          className={selectStyles}
        >
          <option value="raised">Raised</option>
          <option value="collected">Collected</option>
        </select>
      </label>

      <Button size="sm" onClick={onExport} loading={isExporting} leftIcon={<span>📥</span>}>
        Export Excel
      </Button>

      <p className="text-[11.5px] text-[var(--color-ink-muted)] md:ml-auto md:max-w-xs md:text-right">
        {basis === 'collected'
          ? 'Only payments an admin has verified appear.'
          : 'Every payment raised in the range, pending ones included.'}
      </p>
    </div>

    {rangeErrors.from || rangeErrors.to || exportError ? (
      <p className="text-[12px] text-[var(--color-danger)]">
        {rangeErrors.from ?? rangeErrors.to ?? exportError}
      </p>
    ) : null}
  </div>
);

export default PaymentsExportBar;
